import React, { useCallback, useContext, useEffect, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import clone from 'clone';
import { isEmpty } from 'lodash';
import { useNavigate } from 'react-router-dom';
import * as styles from '../companies.styles';
import CompanyFormType from '../enums/company-form-types';
import Company from '../models/company';
import { createCompany, updateCompany } from '../services/companies.service';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import Column from '../../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../../core/components/flex/flex.component';
import LabeledInput from '../../../core/components/input/labeled-input.component';
import notify from '../../../core/components/notify/notify.component';
import Row from '../../../core/components/row/row.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import stringIsNullOrWhitespace from '../../../core/utils/validation-utils';
import CommonStyles from '../../../core/common.styles';
import RouteConstants from '../../../core/routes.constants';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';

interface Props {
  readonly isCreatingCompany: boolean;
  readonly companyId: string | undefined;
  readonly company: Company | undefined;
  readonly onSaved?: () => void;
}

const CompanyInfoGroup = ({ isCreatingCompany, companyId, company, onSaved }: Props): React.JSX.Element => {
  const keycloak = useKeycloak();
  const [isSaving, setIsSaving] = useState(false);

  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();

  const [isEdited, setIsEdited] = useState(false);
  const [errors, setErrors] = useState(new Map<CompanyFormType, string | boolean>());

  const [companyToUpdate, setCompanyToUpdate] = useState<Company>(() => company ?? new Company());

  const navigate = useNavigate();

  useEffect(() => {
    if (company != null) {
      setCompanyToUpdate(company);
    }
  }, [company]);

  const handleChange = (formType: CompanyFormType, value: string): void => {
    const companyCopy: Company = { ...clone(companyToUpdate) };
    const newErrors = new Map<CompanyFormType, string | boolean>();

    switch (formType) {
      case CompanyFormType.name:
        companyCopy.name = value;
        if (stringIsNullOrWhitespace(value)) {
          newErrors.set(CompanyFormType.name, true);
        }
        break;
      case CompanyFormType.contactName:
        companyCopy.contactName = value;
        break;
      case CompanyFormType.contactEmail:
        companyCopy.contactEmail = value;
        break;
      case CompanyFormType.contactPhoneNumber:
        companyCopy.contactPhoneNumber = value;
        break;
      case CompanyFormType.contactAddress:
        companyCopy.contactAddress = value;
        break;
      case CompanyFormType.contactCity:
        companyCopy.contactCity = value;
        break;
      default:
        break;
    }

    setErrors(newErrors);
    setCompanyToUpdate(companyCopy);
    setIsEdited(true);
  };

  const saveCompany = useCallback(
    async (newCompany: Company): Promise<void> => {
      if (!isEmpty(errors)) {
        return;
      }
      if (stringIsNullOrWhitespace(newCompany.name)) {
        setErrors(new Map<CompanyFormType, string | boolean>([[CompanyFormType.name, true]]));
        notify(t('company-name-empty-message'), false);
        return;
      }

      setIsSaving(true);
      let companyResult;
      if (isCreatingCompany) {
        companyResult = await createCompany(newCompany, keycloak);
      } else {
        const companyToSend = newCompany;
        companyToSend.id = companyId ?? '';
        companyResult = await updateCompany(companyToSend, keycloak);
      }
      if (companyResult != null) {
        notify(t('company-saved-success-message'), true);
        if (!isCreatingCompany) {
          onSaved?.();
        }
      }
      setIsSaving(false);
      setIsEdited(false);

      if (isCreatingCompany && companyResult != null) {
        navigate(`/${RouteConstants.PATH_COMPANIES}`);
      }
    },
    [keycloak, t, errors, isCreatingCompany, navigate, companyId, onSaved],
  );

  return (
    <div className={style(CommonStyles.sectionCard(isMobile))}>
      <Column crossAxisAlignment={CrossAxisAlignment.Stretch}>
        <Row>
          <span className={style(styles.columnHeader(isMobile, 8))}>{t('company-details').toUpperCase()}</span>
        </Row>

        <div className={style(styles.horizontalDivider)} />

        <div>
          <LabeledInput
            maxLength={320}
            labelText={t('company-name').toString()}
            value={companyToUpdate.name}
            onChange={(value: string): void => {
              handleChange(CompanyFormType.name, value);
            }}
          />
          {errors.get(CompanyFormType.name) === true && (
            <span className={style(styles.labelError)}>{t('company-name-empty-message')}</span>
          )}
          <LabeledInput
            maxLength={320}
            labelText={t('company-contact-name').toString()}
            value={companyToUpdate.contactName}
            onChange={(value: string): void => {
              handleChange(CompanyFormType.contactName, value);
            }}
          />
          <LabeledInput
            maxLength={320}
            labelText={t('company-contact-email').toString()}
            value={companyToUpdate.contactEmail}
            onChange={(value: string): void => {
              handleChange(CompanyFormType.contactEmail, value);
            }}
          />
          <LabeledInput
            maxLength={60}
            labelText={t('company-contact-phone').toString()}
            value={companyToUpdate.contactPhoneNumber}
            onChange={(value: string): void => {
              handleChange(CompanyFormType.contactPhoneNumber, value);
            }}
          />
          <LabeledInput
            maxLength={320}
            labelText={t('company-contact-address').toString()}
            value={companyToUpdate.contactAddress}
            onChange={(value: string): void => {
              handleChange(CompanyFormType.contactAddress, value);
            }}
          />
          <LabeledInput
            maxLength={320}
            labelText={t('company-contact-city').toString()}
            value={companyToUpdate.contactCity}
            onChange={(value: string): void => {
              handleChange(CompanyFormType.contactCity, value);
            }}
          />

          <Row
            mainAxisAlignment={MainAxisAlignment.End}
            crossAxisAlignment={CrossAxisAlignment.Center}
            className={style(styles.label)}
            itemSpacing={8}
          >
            {isEdited && <span className={style(styles.saveMessage)}>{t('there-are-unsaved-changes')}</span>}
            <div className={style(styles.button(isMobile))}>
              <PrimaryButton
                loading={isSaving}
                disabled={!isEdited}
                onClick={(): void => {
                  saveCompany(companyToUpdate);
                }}
              >
                {t('save')}
              </PrimaryButton>
            </div>
          </Row>
        </div>
      </Column>
    </div>
  );
};

export default CompanyInfoGroup;
